import { useTranslation } from 'react-i18next';
import { FieldData, PARTNER_ACCOUNT_STATUSES, Select, TextField, useForm } from 'vopay';
import usePartnerAccountStatusLabel from '@/components/Entities/hooks/usePartnerAccountStatusLabel';
import type { PartnerAccountStatusFilter } from '@/components/Entities/hooks/useApi/useFetchPartnerAccounts';

/** Field names double as the inputs' `name`s (Cypress selects on `select-Status-trigger` etc.). */
export type AccountFilterValues = {
  Country: string;
  Currency: string;
  Status: PartnerAccountStatusFilter;
  SearchText: string;
};

const CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP'];

type UseAccountFilterFormConfigProps = {
  /** Comes from the URL, so the form re-seeds whenever the query string changes. */
  initialValues: AccountFilterValues;
  onSubmit: (values: AccountFilterValues) => void;
};

export default function useAccountFilterFormConfig({ initialValues, onSubmit }: UseAccountFilterFormConfigProps) {
  const config = useConfig();

  const { ctx, renderFormElements } = useForm<AccountFilterValues>({
    initialValues,
    config,
    onSubmit,
    enableReinitialize: true,
  });

  return { ctx, renderFormElements: () => renderFormElements({ config }) };
}

function useConfig(): FieldData<AccountFilterValues>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.List.SearchForm' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();

  const allOption = { label: t('allOption'), value: 'all' };

  // Raw API country names stay the option values (they are what the request filters on); only the
  // labels are localized.
  const countryOptions = [
    allOption,
    { label: tGlobal('Countries.CA'), value: 'Canada' },
    { label: tGlobal('Countries.USLong'), value: 'United States' },
  ];

  const currencyOptions = [allOption, ...CURRENCIES.map((currency) => ({ label: currency, value: currency }))];

  // Only statuses an account can hold (spec 5.4); the label resolver keeps the raw value as the key.
  const statusOptions = [
    allOption,
    ...PARTNER_ACCOUNT_STATUSES.map((status) => ({ label: resolveStatusLabel(status), value: status })),
  ];

  return [
    {
      fieldName: 'Country',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          {...getFormikData<AccountFilterValues['Country']>({})}
          placeholder={t('countryPlaceholder')}
          options={countryOptions}
          sort={false}
        />
      ),
    },
    {
      fieldName: 'Currency',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          {...getFormikData<AccountFilterValues['Currency']>({})}
          placeholder={t('currencyPlaceholder')}
          options={currencyOptions}
          sort={false}
        />
      ),
    },
    {
      fieldName: 'Status',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          {...getFormikData<AccountFilterValues['Status']>({})}
          placeholder={t('statusPlaceholder')}
          options={statusOptions}
          sort={false}
        />
      ),
    },
    {
      fieldName: 'SearchText',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <TextField
          {...getFormikData<AccountFilterValues['SearchText']>({})}
          placeholder={t('keywordPlaceholder')}
          variant="outlineShadow"
        />
      ),
    },
  ];
}
